import {
  createDefaultNode,
  deleteNodeById,
  findNodePath,
  getRootOrder,
  insertNodeAtOverlay,
  listNodeNames,
  normalizeChildOrder,
  patchNodeById,
} from "../utils/layerTree";
import { resolveInlineReplacementUrls } from "../utils/textInlineReplacements";

export const cloneInstance = (instance) => {
  if (!instance) return null;
  return structuredClone(instance);
};

export const resolveInstanceAssetUrl = ({ assets = {}, assetRef }) => {
  const key = String(assetRef || "").trim();
  if (!key) return "";
  const url = assets[key]?.url;
  return typeof url === "string" ? url : "";
};

export const createInstanceNode = ({
  id,
  name,
  existingNames = [],
  type = "text",
}) => {
  const node = createDefaultNode({ id, name, existingNames, type });
  if (type === "text") {
    node.source = { kind: "fixed", value: "" };
  }
  return node;
};

export const flattenInstanceItems = ({
  nodes = {},
  nodeLayouts = {},
  assets = {},
}) => {
  const items = [];
  const walk = ({ map, parentID, originX, originY, parentVisible }) => {
    const ordered = parentID
      ? normalizeChildOrder({
          childOrder: findNodePath({ nodes, nodeID: parentID })?.node
            ?.childOrder,
          children: map,
        })
      : getRootOrder({ nodes: map, nodeLayouts });
    ordered.forEach((id) => {
      const node = map[id];
      if (!node) return;
      const layout = parentID ? node.layout || {} : nodeLayouts[id] || {};
      const box = {
        x: originX + (Number(layout.x) || 0),
        y: originY + (Number(layout.y) || 0),
        width: Number(layout.width) || 0,
        height: Number(layout.height) || 0,
        rotation: Number(layout.rotation) || 0,
        zIndex: Number(layout.zIndex) || 0,
        visible: layout.visible !== false && parentVisible !== false,
      };
      items.push({
        id: node.id,
        parentID,
        node,
        box,
        url:
          node.type === "image"
            ? resolveInstanceAssetUrl({
                assets,
                assetRef: node.source?.assetRef,
              })
            : "",
        text: node.type === "text" ? String(node.source?.value || "") : "",
        inlineReplacementUrls:
          node.type === "text"
            ? resolveInlineReplacementUrls({
                replacements: node.style?.inlineReplacements,
                assets,
              })
            : {},
        isBackground: Boolean(node.isBackground) && !parentID,
      });
      if (node.type === "group") {
        walk({
          map: node.children || {},
          parentID: node.id,
          originX: box.x,
          originY: box.y,
          parentVisible: box.visible,
        });
      }
    });
  };
  walk({
    map: nodes,
    parentID: null,
    originX: 0,
    originY: 0,
    parentVisible: true,
  });
  return items;
};

export const getDisplaySiblingIDs = ({
  nodes = {},
  nodeLayouts = {},
  parentID = null,
}) => {
  if (!parentID) {
    return [...getRootOrder({ nodes, nodeLayouts })].reverse();
  }
  const parent = findNodePath({ nodes, nodeID: parentID })?.node;
  if (!parent) return [];
  const childIDs = normalizeChildOrder({
    childOrder: parent.childOrder,
    children: parent.children,
  });
  return [...childIDs].sort((left, right) => {
    const leftZ = Number(parent.children?.[left]?.layout?.zIndex) || 0;
    const rightZ = Number(parent.children?.[right]?.layout?.zIndex) || 0;
    return rightZ - leftZ;
  });
};

export const applySiblingDisplayOrder = ({
  nodes = {},
  nodeLayouts = {},
  parentID = null,
  orderedDisplayIds = [],
}) => {
  const lastIndex = orderedDisplayIds.length - 1;
  if (!parentID) {
    const nextLayouts = { ...nodeLayouts };
    orderedDisplayIds.forEach((id, displayIndex) => {
      if (!nextLayouts[id]) return;
      nextLayouts[id] = {
        ...nextLayouts[id],
        zIndex: lastIndex - displayIndex,
      };
    });
    return { nodes, nodeLayouts: nextLayouts };
  }

  let nextNodes = nodes;
  orderedDisplayIds.forEach((id, displayIndex) => {
    const found = findNodePath({ nodes: nextNodes, nodeID: id });
    if (!found?.node) return;
    nextNodes = patchNodeById({
      nodes: nextNodes,
      nodeID: id,
      patch: {
        layout: {
          ...(found.node.layout || {}),
          zIndex: lastIndex - displayIndex,
        },
      },
    });
  });
  nextNodes = patchNodeById({
    nodes: nextNodes,
    nodeID: parentID,
    patch: {
      childOrder: [...orderedDisplayIds].reverse(),
    },
  });
  return { nodes: nextNodes, nodeLayouts };
};

export const uniqueAssetKey = ({ name, assets = {} }) => {
  const base =
    String(name || "layer")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "layer";
  let next = base;
  let suffix = 2;
  while (assets[next]) {
    next = `${base}_${suffix}`;
    suffix += 1;
  }
  return next;
};

export const addInstanceAsset = ({ assets = {}, name, url }) => {
  const assetRef = uniqueAssetKey({ name, assets });
  return {
    assets: {
      ...assets,
      [assetRef]: {
        type: "image",
        assetID: assetRef,
        mimeType: "image/png",
        url,
      },
    },
    assetRef,
  };
};

export { deleteNodeById, insertNodeAtOverlay, listNodeNames };
